import React, { useState, useEffect } from "react";
import { Button, Label, Modal, TextInput, Select, Card } from "flowbite-react";
import { FaPen, FaFilter, FaTrash, FaPlus } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import Swal from "sweetalert2";
import axios from "axios";
import ReusableDataTable from "../hooks/ReusableDataTable";
import { useSidebar } from "../context/SidebarOpenContext";
import IssueExportButtons from "../component/common/IssueExportButtons";

function IssueEntries({ isTabbed = false }) {
    const [AddModal, setAddModal] = useState(false);
    const [EditModal, setEditModal] = useState(false);
    const [DeleteModal, setDeleteModal] = useState(false);

    const [IssueList, setIssueList] = useState([]);
    const [Orders, setOrders] = useState([]);
    const [RawMaterials, setRawMaterials] = useState([]);
    const [Fabrics, setFabrics] = useState([]);
    const [Buyers, setBuyers] = useState([]);
    const [Styles, setStyles] = useState([]);

    const [Filters, setFilters] = useState({ party: "", item: "", style: "", color: "", startDate: "", endDate: "" });
    
    const getInitialState = () => ({
        issue_date: new Date().toISOString().split('T')[0],
        dc_no: "", 
        order_id: "",
        style_name: "",
        items: [{ item_type: 'trim', item_id: "", quantity: "", size: "" }] 
    });

    const [Input, setInput] = useState(getInitialState());
    const { isSidebarOpen } = useSidebar();

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
    };

    useEffect(() => { fetchIssueEntries(); }, []);

    const uniqueParties = [...new Set(IssueList.map(item => item.buyer_name))].filter(Boolean).sort();
    const uniqueItems = [...new Set(IssueList.map(item => item.item_name))].filter(Boolean).sort();
    const uniqueStyles = [...new Set(IssueList.map(item => item.style_name))].filter(Boolean).sort();
    const uniqueColors = [...new Set(IssueList.map(item => item.colour_name))].filter(c => c && c !== "N/A" && c !== "-").sort();

    const fetchDropdownData = async () => {
        if (Orders.length > 0) return; 
        try {
            const [resOrder, resRM, resFab, resBuyer, resStyle] = await Promise.all([
                axios.get("order/orders"), axios.get("master/raw_material_stocks"),
                axios.get("master/fabrics"), axios.get("master/buyers"), axios.get("master/styles")
            ]);
            setOrders(resOrder.data); setRawMaterials(resRM.data);
            setFabrics(resFab.data); setBuyers(resBuyer.data); setStyles(resStyle.data);
        } catch (err) { console.error(err); }
    };

    const fetchIssueEntries = async () => {
        try {
            const response = await axios.get("issue-entries");
            const sortedData = (response.data || []).sort((a, b) => b.id - a.id);
            setIssueList(sortedData);
        } catch (error) { console.error(error); }
    };

    const handleEditClick = async (row) => {
        await fetchDropdownData(); 
        setInput({
            id: row.id,
            issue_date: row.issue_date,
            dc_no: row.dc_no || "",
            order_id: String(row.order_id || ""),
            style_name: row.style_name,
            items: [{
                item_type: row.item_type || 'trim',
                item_id: String(row.item_id || ""),
                quantity: row.quantity || row.quantity_issued || "",
                size: row.item_size || ""
            }]
        });
        setEditModal(true);
    };

    const handleOrderChange = (e) => {
        const orderId = e.target.value;
        const selectedOrder = Orders.find(o => String(o.order_id) === String(orderId));
        if (selectedOrder) {
            const styleObj = Styles.find(s => String(s.style_id) === String(selectedOrder.style_id));
            setInput({ ...Input, order_id: orderId, style_id: selectedOrder.style_id, style_name: styleObj ? styleObj.style_name : "N/A" });
        }
    };

    const handleInputChange = (e, index = null) => {
        const { name, value } = e.target;
        if (index === null) {
            setInput({ ...Input, [name]: value });
            return;
        }
        let updatedItems = [...Input.items];
        updatedItems[index] = { ...updatedItems[index], [name]: value };
        if (name === "item_type") { updatedItems[index].item_id = ""; updatedItems[index].size = ""; }
        if (name === "item_id" && updatedItems[index].item_type === 'trim') {
            const match = RawMaterials.find(rm => String(rm.raw_material_id) === String(value));
            if (match) updatedItems[index].size = match.size;
        }
        if (index === updatedItems.length - 1 && (updatedItems[index].item_id !== "" || updatedItems[index].quantity !== "")) {
            updatedItems.push({ item_type: 'trim', item_id: "", quantity: "", size: "" });
        }
        setInput({ ...Input, items: updatedItems });
    };

    const removeItemLine = (index) => {
        const newItems = Input.items.filter((_, i) => i !== index);
        if (newItems.length === 0) newItems.push({ item_type: 'trim', item_id: "", quantity: "", size: "" });
        setInput(prev => ({ ...prev, items: newItems }));
    };

    const filteredData = IssueList.filter((item) => {
        const matchParty = Filters.party === "" || item.buyer_name === Filters.party;
        const matchItem = Filters.item === "" || item.item_name === Filters.item;
        const matchStyle = Filters.style === "" || item.style_name === Filters.style;
        const matchColor = Filters.color === "" || item.colour_name === Filters.color;
        const matchDate = (!Filters.startDate || item.issue_date >= Filters.startDate) &&
                          (!Filters.endDate || item.issue_date <= Filters.endDate);
        return matchParty && matchItem && matchStyle && matchColor && matchDate;
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validItems = Input.items.filter(item => item.item_id && item.quantity);
        if (validItems.length === 0) { Swal.fire({ icon: "warning", text: "Please select at least one item and quantity." }); return; }
        const payloadToSubmit = { ...Input, items: validItems };
        const url = EditModal ? `issue-entries/${Input.id}` : "issue-entries";
        try {
            const res = await axios[EditModal ? "put" : "post"](url, payloadToSubmit);
            if (res.data.success === 1) {
                Swal.fire({ icon: "success", text: "Saved Successfully", timer: 1500, showConfirmButton: false });
                setAddModal(false); setEditModal(false); fetchIssueEntries();
            }
        } catch (error) { Swal.fire({ icon: "error", text: error.response?.data?.error || "Error" }); }
    };

    const deleteEntry = async () => {
        try {
            const res = await axios.delete(`issue-entries/${Input.id}`);
            if (res.data.success === 1) {
                Swal.fire({ icon: "success", text: "Deleted", timer: 1500, showConfirmButton: false });
                fetchIssueEntries(); setDeleteModal(false);
            }
        } catch (error) { console.error(error); }
    };

    const columns = [
        { name: "Date", selector: (row) => formatDateForDisplay(row.issue_date), sortable: true },
        { name: "DC No", selector: (row) => row.dc_no || "-", sortable: true },
        { name: "Order", selector: (row) => row.cut_order_no, sortable: true },
        { name: "Customer", selector: (row) => row.buyer_name, sortable: true },
        { name: "Style", selector: (row) => row.style_name, sortable: true },
        { name: "Item", selector: (row) => row.item_name, sortable: true },
        { name: "Size", selector: (row) => row.item_size || "-", sortable: true },
        { name: "Color", selector: (row) => row.colour_name || "-", sortable: true },
        { name: "Qty", selector: (row) => row.quantity_issued, sortable: true },
        { name: "Option", cell: (row) => (
            <div className="flex gap-2">
                <FaPen className="text-amber-400 cursor-pointer" onClick={() => handleEditClick(row)} />
                <MdDeleteForever className="text-red-500 text-xl cursor-pointer" onClick={() => { setInput(row); setDeleteModal(true); }} />
            </div>
        )}
    ];

    const containerClass = isTabbed ? "p-0 bg-transparent" : `${isSidebarOpen ? "p-4 sm:ml-64" : "p-4"} bg-content-bg min-h-screen mt-14`;

    return (
        <div className={containerClass}>
            {/* 1. BUTTONS (Add Left, Exports Right) */}
            <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-xl shadow-sm ring-1 ring-gray-100">
                <Button 
                    className="bg-icon-color text-sidebar-color font-bold hover:opacity-90" 
                    onClick={async () => { await fetchDropdownData(); setInput(getInitialState()); setAddModal(true); }}>
                    <FaPlus className="mr-2"/> Issue Material
                </Button>
                <IssueExportButtons data={filteredData} filename="Stock_Issue_Report" />
            </div>

            {/* 2. FILTERS */}
            <Card className="mb-6 border-0 shadow-sm ring-1 ring-gray-100">
                {/* <div className="flex items-center gap-2 mb-4 text-sidebar-color font-bold border-b border-gray-200 pb-3">
                    <FaFilter /> <span>Report Filters</span>
                </div> */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sidebar-color">
                    <div><Label className="text-sidebar-color" value="Party-wise" /><Select value={Filters.party} onChange={(e) => setFilters({...Filters, party: e.target.value})}><option value="">All Parties</option>{uniqueParties.map(name => <option key={name} value={name}>{name}</option>)}</Select></div>
                    <div><Label className="text-sidebar-color" value="Item-wise" /><Select value={Filters.item} onChange={(e) => setFilters({...Filters, item: e.target.value})}><option value="">All Items</option>{uniqueItems.map(name => <option key={name} value={name}>{name}</option>)}</Select></div>
                    <div><Label className="text-sidebar-color" value="Style-wise" /><Select value={Filters.style} onChange={(e) => setFilters({...Filters, style: e.target.value})}><option value="">All Styles</option>{uniqueStyles.map(name => <option key={name} value={name}>{name}</option>)}</Select></div>
                    <div><Label className="text-sidebar-color" value="Color-wise" /><Select value={Filters.color} onChange={(e) => setFilters({...Filters, color: e.target.value})}><option value="">All Colors</option>{uniqueColors.map(name => <option key={name} value={name}>{name}</option>)}</Select></div>
                    <div><Label className="text-sidebar-color" value="From Date" /><TextInput type="date" value={Filters.startDate} onChange={(e) => setFilters({...Filters, startDate: e.target.value})} /></div>
                    <div><Label className="text-sidebar-color" value="To Date" /><TextInput type="date" value={Filters.endDate} onChange={(e) => setFilters({...Filters, endDate: e.target.value})} /></div>
                </div>
            </Card>

            {/* 3. TABLE */}
            <div className={isTabbed ? "" : "p-4 rounded-lg shadow-sm bg-white ring-1 ring-gray-100"}>
                <ReusableDataTable title="" columns={columns} data={filteredData} />
            </div>

            {/* MODALS REMAIN THE SAME */}
            <Modal show={AddModal || EditModal} size="4xl" onClose={() => { setAddModal(false); setEditModal(false); }}>
                <Modal.Header className="text-sidebar-color border-b border-gray-200">
                    <span className="text-xl font-bold text-sidebar-color">{EditModal ? "Update" : "Add"} Issue Entry</span>
                </Modal.Header>
                <Modal.Body className="max-h-[75vh] overflow-y-auto bg-gray-50/30">
                    <form id="issueForm" onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-sidebar-color">
                            <div><Label className="text-sidebar-color" value="Issue Date" /><TextInput name="issue_date" type="date" value={Input.issue_date} onChange={handleInputChange} required /></div>
                            <div><Label className="text-sidebar-color" value="DC No" /><TextInput name="dc_no" type="text" placeholder="DV0023MM" value={Input.dc_no} onChange={handleInputChange} required /></div>
                            <div><Label className="text-sidebar-color" value="Order" /><Select name="order_id" value={String(Input.order_id || "")} onChange={handleOrderChange} required><option value="">Select Order</option>{Orders.map(o => <option key={o.order_id} value={String(o.order_id)}>{o.cut_order_no}</option>)}</Select></div>
                            <div><Label className="text-sidebar-color" value="Style" /><TextInput name="style_name" value={Input.style_name || ""} disabled /></div>
                        </div>
                        <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm max-h-[45vh] overflow-y-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs uppercase bg-sidebar-color text-white sticky top-0 z-10 shadow-sm">
                                    <tr><th className="px-4 py-3 font-semibold">Item Type</th><th className="px-4 py-3 font-semibold">Select Item</th><th className="px-4 py-3 font-semibold">Size</th><th className="px-4 py-3 font-semibold">Quantity</th><th className="px-4 py-3 font-semibold text-center w-16">Action</th></tr>
                                </thead>
                                <tbody>
                                    {Input.items.map((item, index) => (
                                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                                            <td className="px-4 py-3"><Select name="item_type" value={item.item_type} onChange={(e) => handleInputChange(e, index)}><option value="trim">Trims</option><option value="fabric">Fabric</option></Select></td>
                                            <td className="px-4 py-3 min-w-[200px]"><Select name="item_id" value={String(item.item_id || "")} onChange={(e) => handleInputChange(e, index)}><option value="">Select Item</option>{item.item_type === 'trim' ? RawMaterials.map(rm => (<option key={rm.raw_material_id} value={String(rm.raw_material_id)}>{rm.raw_material_name} ({rm.size || ""})</option>)) : Fabrics.map(f => <option key={f.fabric_id} value={String(f.fabric_id)}>{f.fabric_content} ({f.roll_no})</option>)}</Select></td>
                                            <td className="px-4 py-3 w-32">{item.item_type === 'trim' && item.size ? <TextInput value={item.size} disabled className="bg-gray-50" /> : <span className="text-gray-400 pl-2">-</span>}</td>
                                            <td className="px-4 py-3 w-36"><TextInput name="quantity" type="number" step="0.01" value={item.quantity} onChange={(e) => handleInputChange(e, index)} /></td>
                                            <td className="px-4 py-3 text-center">{Input.items.length > 1 && <button type="button" onClick={() => removeItemLine(index)} className="text-red-500 hover:bg-red-50 p-2 rounded-full"><FaTrash size={16} /></button>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer className="flex justify-center gap-3 bg-gray-50 border-t border-gray-200">
                    <Button className="bg-icon-color text-sidebar-color font-bold hover:opacity-90" type="submit" form="issueForm">Save Entries</Button>
                    <Button color="light" onClick={() => { setAddModal(false); setEditModal(false); }}>Cancel</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={DeleteModal} size="md" onClose={() => setDeleteModal(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="text-center">
                        <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-red-500" />
                        <h3 className="mb-5 text-lg text-sidebar-color font-semibold">Restore stock and delete entry?</h3>
                        <div className="flex justify-center gap-4">
                            <Button color="failure" onClick={deleteEntry}>Yes, Delete</Button>
                            <Button color="light" onClick={() => setDeleteModal(false)}>No, Cancel</Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default IssueEntries;